import { FeedTypeAndComponent } from "@/features/feed";
import useCloudFunctionCallForListsAndInfiniteScroll from "@/hooks/useCloudFunctionCallForLists";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { siteRoutes } from "@/routes/siteRoutesList";
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Flex, Heading, Text } from "@radix-ui/themes";
import i18next from "i18next";
import { Link } from "react-router-dom";

export const ProfileTabDataMobileFetcher: FC<{}> = ({}) => {
  const { slug } = useParams();
  const [exists, setExists] = useState<boolean>(true);
  const currentUser = useCurrentUserProfile((s) => s.user);
  const navigate = useNavigate();
  const accessibleTypes = [
    {
      name: "challenges",
      gridStyles: "challenges-list",
      featureAvailable: brand.hasFeature.challenges,
    },
    {
      name: "teams",
      gridStyles: "teams-overview",
      featureAvailable: brand.hasFeature.teams,
    },
    {
      name: "achievements",
      gridStyles: "",
      featureAvailable: brand.hasFeature.achievements,
    },
  ];
  const currentActiveType = accessibleTypes.find((s) => s.name === slug);

  const [
    data, // this feed will be either posts/challenges/gathers/teams,etc based on feedType
    last,
    hasMore,
    _loading,
    error,
    fetchData,
    ref,
    inView,
  ] = useCloudFunctionCallForListsAndInfiniteScroll<any[] | undefined, any>(
    "getUserFeedBasedOnSlug",
    currentUser.slug,
    `${currentUser.slug}_${slug}`, //filter key
    true // to persist the data in the local storage.
  );

  const params: GetUserFeedBasedOnSlugProps = {
    slug: currentUser.slug,
    lastFeedDateInSeconds: last || undefined,
    type: slug as "posts" | "challenges" | "teams" | "achievements",
  };

  const ElementToRender = useMemo(() => {
    return FeedTypeAndComponent[slug];
  }, [slug]);

  useEffect(() => {
    if (slug && currentActiveType) {
      if (currentActiveType && !currentActiveType.featureAvailable) {
        return setExists(false);
      }
      (async () => {
        fetchData(
          {
            slug: currentUser.slug,
            lastFeedDateInSeconds: undefined,
            type: slug,
          },
          `${currentUser.slug}_${slug}`,
          false
        );
      })();

      return setExists(true);
    } else {
      return setExists(false);
    }
  }, [slug]);

  const fetchMore = async () => {
    await fetchData(params, `${currentUser.slug}_${slug}`, true);
  };

  useEffect(() => {
    if (inView) {
      fetchMore();
    }
  }, [inView]);

  const inViewRefElementIndex = useMemo(() => {
    return data ? data?.length - 4 : -1;
  }, [data, hasMore]);

  // Variables
  const noChallenges =
    currentActiveType.name === "challenges" && data?.length === 0;

  const heading = [
    {
      value: "challenges",
      label: i18next.t("profile.tabs.challenges"),
    },
    {
      value: "teams",
      label: i18next.t("profile.tabs.teams"),
    },
    {
      value: "achievements",
      label: i18next.t("profile.tabs.achievements"),
    },
    {
      value: "gathers",
      label: i18next.t("profile.tabs.gathers"),
    },
    {
      value: "community",
      label: i18next.t("profile.tabs.community"),
    },
    {
      value: "posts",
      label: i18next.t("profile.tabs.posts"),
    },
  ];

  return (
    <>
      {exists && (
        <Box>
          <Box>
            <Button
              variant="solid"
              onClick={() => navigate(siteRoutes["profile"])}
            >
              {i18next.t("buttons.go_back")}
            </Button>
            {heading.map((head) => {
              if (head.value === slug) {
                return (
                  <Heading as="h1" key={head.value}>
                    {head.label}
                  </Heading>
                );
              }
            })}
          </Box>

          <Box>
            {(data || []).map((dataObj, ind) => {
              const key = dataObj.slug;

              return (
                <Flex
                  direction="column"
                  gap="3"
                  key={key}
                  ref={ind === inViewRefElementIndex ? ref : null}
                >
                  {/* Render cards */}
                  <ElementToRender props={{ ...dataObj, error }} />
                </Flex>
              );
            })}

            {/* If the function call fails */}
            {error && (
              <Text as="p" size="3" color="gray">
                {i18next.t("profile.tabs.error")}
              </Text>
            )}

            {/* Not loading, no challenges, and the create feature is enabled */}
            {!_loading && noChallenges && brand.hasCreate.challenges && (
              <Text as="p" size="3" color="gray">
                {i18next.t("profile.tabs.mobile.noChallenges")}{" "}
                <Link to={siteRoutes["createChallenge"]}>
                  {i18next.t("profile.tabs.mobile.createChallenge")}
                </Link>
              </Text>
            )}
          </Box>
        </Box>
      )}

      {/* If nothing found */}
      {!exists && (
        <Box>
          {heading.map((head) => {
            if (head.value === slug) {
              return (
                <Heading as="h1" key={head.value}>
                  {i18next.t("profile.tabs.mobile.nothingFound")}{" "}
                  {head.label.toLowerCase()}.
                </Heading>
              );
            }
          })}
          <Button
            variant="solid"
            onClick={() => navigate(siteRoutes["profile"])}
          >
            {i18next.t("buttons.go_back")}
          </Button>
        </Box>
      )}
    </>
  );
};
